import React from 'react';
import { getCurrencySymbol } from '../../utils/currencyUtils';
import { ITEMS_THEME, formFieldCls, formLabelCls } from './itemsTheme';
import { ChevronDown, Plus } from 'lucide-react';
import AppSelect from '../shared/AppSelect';

function Field({ label, htmlFor, children, className = '' }) {
  return (
    <div className={className}>
      {label && (
        <label htmlFor={htmlFor} className={formLabelCls}>
          {label}
        </label>
      )}
      {children}
    </div>
  );
}

export default function ItemFormPanel({
  embedded = false,
  editingItemId,
  companyCurrency,
  t,
  itemName,
  setItemName,
  quantityMeasurement,
  setQuantityMeasurement,
  unitSearch,
  setUnitSearch,
  filteredUnits,
  predefinedUnits,
  customUnit,
  setCustomUnit,
  defaultRate,
  setDefaultRate,
  itemType,
  setItemType,
  gstPercentage,
  setGstPercentage,
  hsnCode,
  setHsnCode,
  isActive,
  setIsActive,
  category,
  setCategory,
  customCategory,
  setCustomCategory,
  predefinedCategories,
  companyCategories,
  barcodeNumber,
  setBarcodeNumber,
  compositionGstRate,
  setCompositionGstRate,
  purchasePrice,
  setPurchasePrice,
  salePrice,
  setSalePrice,
  openingStock,
  setOpeningStock,
  description,
  setDescription,
  isRawMaterial,
  setIsRawMaterial,
  rawMaterialType,
  setRawMaterialType,
  defaultServiceTagsInput,
  setDefaultServiceTagsInput,
  onSave,
  onClear,
}) {
  const theme = ITEMS_THEME;
  const field = `${formFieldCls} ${theme.fieldFocus}`;
  const selectTrigger = `${field} text-left min-h-[44px] flex items-center justify-between touch-manipulation`;
  const sym = getCurrencySymbol(companyCurrency);
  const grid = 'grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-2 2xl:grid-cols-3 gap-x-3 gap-y-2';

  const formBody = (
    <>
      {!embedded && (
        <div
          className={`flex items-center justify-between gap-2 mb-3 -mx-1 px-3 py-2.5 rounded-xl border ${theme.formHeader} ${theme.detailsBorder}`}
        >
          <h2 className={`text-sm font-bold ${theme.formTitle}`}>
            {editingItemId ? 'Edit item' : 'New item'}
          </h2>
          {editingItemId && (
            <span className="text-[10px] font-semibold uppercase tracking-wide text-amber-800 bg-amber-50 border border-amber-200 px-2 py-0.5 rounded">
              Editing
            </span>
          )}
        </div>
      )}

      <div className={grid}>
        <Field label="Item name" htmlFor="itemName" className="sm:col-span-2">
          <input
            type="text"
            id="itemName"
            value={itemName}
            onChange={(e) => setItemName(e.target.value)}
            className={field}
            placeholder="e.g., Flex Printing"
            required
          />
        </Field>

        <div className="relative">
          <label htmlFor="quantityMeasurement" className={formLabelCls}>
            Unit
          </label>
          <input
            type="text"
            id="quantityMeasurement"
            value={quantityMeasurement}
            onChange={(e) => {
              setQuantityMeasurement(e.target.value);
              setUnitSearch(e.target.value);
            }}
            onFocus={() => setUnitSearch(quantityMeasurement)}
            className={field}
            placeholder="e.g., Sq. Ft"
            autoComplete="off"
            required
          />
          {unitSearch && (
            <div className="absolute z-50 w-full mt-1 bg-white border border-slate-200 rounded-lg shadow-lg max-h-40 overflow-auto">
              {filteredUnits.map((unit) => (
                <button
                  key={unit}
                  type="button"
                  onMouseDown={() => {
                    setQuantityMeasurement(unit);
                    setUnitSearch('');
                    if (unit !== 'Other') setCustomUnit('');
                  }}
                  className={`w-full text-left px-3 py-1.5 text-sm ${theme.dropdownHover}`}
                >
                  {unit}
                </button>
              ))}
            </div>
          )}
          {((quantityMeasurement === 'Other') ||
            (!predefinedUnits.includes(quantityMeasurement) && quantityMeasurement)) && (
            <input
              type="text"
              id="customUnit"
              value={customUnit}
              onChange={(e) => {
                setCustomUnit(e.target.value);
                setQuantityMeasurement(e.target.value);
              }}
              className={`${field} mt-1`}
              placeholder="Custom unit"
              required
            />
          )}
        </div>

        <Field label={`Rate (${sym})`} htmlFor="defaultRate">
          <input
            type="number"
            id="defaultRate"
            value={defaultRate}
            onChange={(e) => setDefaultRate(e.target.value)}
            className={field}
            placeholder="0"
            inputMode="decimal"
          />
        </Field>

        <Field label="Type" htmlFor="itemType">
          <AppSelect
            id="itemType"
            value={itemType}
            onChange={(e) => setItemType(e.target.value)}
            aria-label="Item type"
            sheetTitle="Item type"
            options={[
              { value: 'Goods', label: t('goods') },
              { value: 'Services', label: t('services') },
              { value: 'Restaurant', label: t('restaurant') },
              { value: 'Other', label: t('other') },
            ]}
            triggerClassName={selectTrigger}
          />
        </Field>

        <Field label="GST %" htmlFor="gstPercentage">
          <input
            type="number"
            id="gstPercentage"
            value={gstPercentage}
            onChange={(e) => setGstPercentage(e.target.value)}
            className={field}
            min="0"
            max="100"
            step="0.01"
            required
          />
        </Field>

        <Field label="HSN code" htmlFor="hsnCode">
          <input
            type="text"
            id="hsnCode"
            value={hsnCode}
            onChange={(e) => setHsnCode(e.target.value)}
            className={field}
            placeholder="48025510"
          />
        </Field>

        <div className="flex items-end pb-1">
          <label htmlFor="isActive" className="inline-flex items-center gap-2 cursor-pointer text-sm text-slate-700">
            <input
              type="checkbox"
              id="isActive"
              checked={isActive}
              onChange={(e) => setIsActive(e.target.checked)}
              className={`h-4 w-4 rounded border-slate-300 ${theme.checkbox}`}
            />
            Active
          </label>
        </div>

        <Field label="Category" htmlFor="category">
          <AppSelect
            id="category"
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            placeholder="Select category"
            aria-label="Category"
            sheetTitle="Category"
            options={[
              { value: '', label: 'Select category' },
              ...predefinedCategories.map((cat) => ({ value: cat, label: cat })),
              ...companyCategories.map((cat) => ({ value: cat, label: cat })),
              { value: 'Other', label: 'Add new…' },
            ]}
            triggerClassName={selectTrigger}
          />
          {category === 'Other' && (
            <input
              type="text"
              id="customCategory"
              value={customCategory}
              onChange={(e) => setCustomCategory(e.target.value)}
              className={`${field} mt-1`}
              placeholder="Category name"
              required
            />
          )}
        </Field>

        <Field label="Barcode" htmlFor="barcodeNumber">
          <input
            type="text"
            id="barcodeNumber"
            value={barcodeNumber}
            onChange={(e) => setBarcodeNumber(e.target.value)}
            className={field}
          />
        </Field>

        <Field label="Composition GST %" htmlFor="compositionGstRate">
          <input
            type="number"
            id="compositionGstRate"
            value={compositionGstRate}
            onChange={(e) => setCompositionGstRate(e.target.value)}
            className={field}
            min="0"
            max="100"
            step="0.01"
          />
        </Field>

        <Field label="Purchase price" htmlFor="purchasePrice">
          <input
            type="number"
            id="purchasePrice"
            value={purchasePrice}
            onChange={(e) => setPurchasePrice(e.target.value)}
            className={field}
            min="0"
          />
        </Field>

        <Field label="Sale price" htmlFor="salePrice">
          <input
            type="number"
            id="salePrice"
            value={salePrice}
            onChange={(e) => setSalePrice(e.target.value)}
            className={field}
            min="0"
          />
        </Field>

        <Field label="Opening stock" htmlFor="openingStock">
          <input
            type="number"
            id="openingStock"
            value={openingStock}
            onChange={(e) => setOpeningStock(e.target.value)}
            className={field}
            min="0"
          />
        </Field>
      </div>

      <details className={`mt-3 group rounded-lg border ${theme.detailsBorder} bg-white`}>
        <summary className="flex cursor-pointer list-none items-center justify-between gap-2 px-3 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-50 rounded-lg">
          <span>More options</span>
          <ChevronDown className="h-4 w-4 text-slate-400 transition-transform group-open:rotate-180" />
        </summary>
        <div className="px-3 pb-3 space-y-2 border-t border-slate-100">
          <Field label="Description" htmlFor="description">
            <textarea
              id="description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={2}
              className={`${field} resize-none`}
              placeholder={t('itemDescription')}
            />
          </Field>
          <div>
            <label className="inline-flex items-center gap-2 cursor-pointer text-sm text-slate-700">
              <input
                type="checkbox"
                checked={isRawMaterial}
                onChange={(e) => setIsRawMaterial(e.target.checked)}
                className={`h-4 w-4 rounded border-slate-300 ${theme.checkbox}`}
              />
              Raw material (goods)
            </label>
            {isRawMaterial && (
              <AppSelect
                id="rawMaterialType"
                value={rawMaterialType}
                onChange={(e) => setRawMaterialType(e.target.value)}
                aria-label="Raw material type"
                sheetTitle="Raw material type"
                options={[
                  { value: 'Base Material', label: t('baseMaterial') },
                  { value: 'Consumable', label: t('consumable') },
                  { value: 'Packing Material', label: t('packingMaterial') },
                  { value: 'Other', label: t('other') },
                ]}
                triggerClassName={`${selectTrigger} mt-2`}
              />
            )}
          </div>
          <Field label="Service tags (Sales)" htmlFor="defaultServiceTags">
            <input
              id="defaultServiceTags"
              type="text"
              value={defaultServiceTagsInput}
              onChange={(e) => setDefaultServiceTagsInput(e.target.value)}
              className={field}
              placeholder="pasting, frame, lamination"
            />
            <p className="text-[11px] text-slate-500 mt-1">Comma-separated for Sales billing.</p>
          </Field>
        </div>
      </details>

      <div className="flex flex-col sm:flex-row gap-2 mt-4 pt-3 border-t border-slate-100">
        <button
          type="button"
          id="add-item-button"
          onClick={onSave}
          className={`flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2.5 min-h-[44px] text-sm font-semibold text-white rounded-xl bg-gradient-to-r ${theme.btn} shadow-sm ${theme.btnRing} focus:outline-none focus:ring-2 focus:ring-offset-1`}
        >
          <Plus className="h-4 w-4 shrink-0" aria-hidden />
          {editingItemId ? 'Update' : 'Save item'}
        </button>
        <button
          type="button"
          onClick={onClear}
          className={`flex-1 sm:flex-none px-4 py-2.5 min-h-[44px] text-sm font-semibold bg-white border rounded-xl ${theme.btnSecondary}`}
        >
          {editingItemId ? 'Cancel' : 'Clear'}
        </button>
      </div>
    </>
  );

  if (embedded) {
    return <div>{formBody}</div>;
  }

  return (
    <div className="rounded-xl border border-slate-200 bg-white shadow-sm overflow-hidden">
      <div className="p-4">{formBody}</div>
    </div>
  );
}
